import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../api/homeapi";
import { StaffFrame, PageHead, T } from "./DashboardShared";
import { useAuth } from "../context/AuthContext";
import { useNotify, errorMessage } from "../components/Notify";
import { InboxIcon, MessageIcon, PinIcon, PlusIcon, SendIcon, PhoneIcon, WhatsAppIcon, TrashIcon, UsersIcon } from "../components/Icons";

// Actualisation automatique de la messagerie - pas besoin de recharger la
// page pour voir les nouveaux messages.
const POLL_MS = 5000;

const TABS = [
  { id: "INBOX",    label: "Boîte de réception",    short: "Réception", icon: InboxIcon },
  { id: "INTERNAL", label: "Communication interne", short: "Interne",   icon: MessageIcon },
  { id: "WALL",     label: "Mur d'annonces",        short: "Annonces",  icon: PinIcon },
];

const css = `
.msg-tabs{display:flex;gap:8px;margin-bottom:18px;overflow-x:auto;scrollbar-width:none;}
.msg-tabs::-webkit-scrollbar{display:none;}
.msg-tab{display:flex;align-items:center;gap:7px;padding:9px 15px;border-radius:11px;border:1.5px solid ${T.creamDk};background:${T.white};color:${T.smoke};font-family:'DM Sans',sans-serif;font-weight:700;font-size:13px;cursor:pointer;white-space:nowrap;flex-shrink:0;transition:all .15s;}
.msg-tab.active{border-color:${T.forest};background:#e8f1ec;color:${T.forestDk};}
.msg-tab .tab-short{display:none;}
.msg-layout{display:grid;grid-template-columns:300px minmax(0,1fr);gap:16px;min-height:480px;}
.msg-pane{border:1.5px solid ${T.creamDk};border-radius:14px;background:${T.white};overflow:hidden;display:flex;flex-direction:column;min-width:0;}
.msg-list{overflow-y:auto;max-height:560px;}
.msg-tools{display:flex;border-bottom:1.5px solid ${T.creamDk};}
.msg-tools button{display:flex;align-items:center;gap:6px;justify-content:center;flex:1;padding:11px;border:none;background:#fff;font-family:'DM Sans',sans-serif;font-weight:700;font-size:12.5px;color:${T.forestDk};cursor:pointer;}
.msg-tools button + button{border-left:1.5px solid ${T.creamDk};}
.msg-tools button.on{background:#e8f1ec;}
.conv{padding:12px 14px;border-bottom:1px solid ${T.creamDk};cursor:pointer;transition:background .12s;}
.conv:hover{background:#faf7f0;}
.conv.active{background:#e8f1ec;}
.conv-top{display:flex;justify-content:space-between;align-items:center;gap:8px;}
.conv-name{font-weight:700;font-size:13.5px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.conv-dot{width:8px;height:8px;border-radius:50%;background:${T.gold};flex-shrink:0;}
.conv-sub{font-size:12px;color:#4b5a53;margin-top:2px;}
.conv-last{font-size:11.5px;color:#93a39a;margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.th-head{display:flex;align-items:center;gap:10px;padding:12px 16px;border-bottom:1.5px solid ${T.creamDk};}
.th-back{display:none;}
.th-title{font-weight:700;font-size:15px;overflow-wrap:anywhere;}
.th-meta{font-size:12px;color:${T.smoke};overflow-wrap:anywhere;}
.th-lead{padding:12px 16px;background:#fffbef;border-bottom:1.5px solid ${T.creamDk};}
.th-lead p{font-size:12.5px;color:#92400e;margin:0 0 10px;line-height:1.5;}
.th-lead .btn-row a{display:inline-flex;align-items:center;gap:6px;border-radius:9px;padding:9px 14px;font-size:13px;font-weight:700;text-decoration:none;color:#fff;}
.th-msgs{flex:1;overflow-y:auto;padding:16px;display:flex;flex-direction:column;gap:10px;max-height:420px;}
.bubble-wrap{max-width:78%;}
.bubble{border-radius:14px;padding:9px 13px;font-size:13.5px;line-height:1.45;white-space:pre-wrap;overflow-wrap:anywhere;}
.bubble-meta{font-size:10.5px;color:${T.smoke};margin-top:3px;}
.th-reply{display:flex;gap:8px;padding:12px;border-top:1.5px solid ${T.creamDk};}
.th-reply input{flex:1;min-width:0;}
.th-empty{margin:auto;color:${T.smoke};font-size:13px;padding:30px;text-align:center;}
.new-box{padding:12px;border-bottom:1.5px solid ${T.creamDk};background:#faf7f0;display:flex;flex-direction:column;gap:8px;}
.ann{border:1.5px solid ${T.creamDk};border-radius:14px;padding:16px;margin-bottom:12px;background:${T.white};}
.ann.pinned{border-color:${T.gold};background:#fffbef;}
.ann-head{display:flex;justify-content:space-between;align-items:flex-start;gap:10px;}
.ann-title{display:flex;align-items:center;gap:6px;font-weight:700;font-size:15px;overflow-wrap:anywhere;}
.react{display:flex;align-items:center;gap:4px;font-size:13px;padding:5px 10px;border-radius:20px;cursor:pointer;font-family:inherit;}
@media(max-width:900px){
  .msg-layout{grid-template-columns:1fr;min-height:0;}
  .msg-layout.has-thread .msg-list-pane{display:none;}
  .msg-layout:not(.has-thread) .msg-thread-pane{display:none;}
  .th-back{display:inline-flex;}
  .msg-list{max-height:none;}
  .th-msgs{max-height:none;min-height:240px;}
  .msg-thread-pane{min-height:calc(100vh - 260px);}
}
@media(max-width:520px){
  .msg-tab .tab-long{display:none;}
  .msg-tab .tab-short{display:inline;}
  .msg-tab{flex:1;justify-content:center;padding:9px 10px;}
  .bubble-wrap{max-width:88%;}
  .th-reply .btn .send-label{display:none;}
}
`;

export default function AdminMessages() {
  const { user } = useAuth();
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const isAdmin = roles.includes("ROLE_ADMIN");
  const isSupport = roles.includes("ROLE_SUPPORT");
  const canSeeInbox = isAdmin || isSupport;

  const visibleTabs = TABS.filter(t => t.id !== "INBOX" || canSeeInbox);
  const [tab, setTab] = useState(canSeeInbox ? "INBOX" : "INTERNAL");

  // Éteint le badge de notification du menu latéral dès que l'utilisateur
  // consulte un onglet de la messagerie.
  useEffect(() => {
    try { localStorage.setItem("messages_last_seen_at", new Date().toISOString()); } catch { /* stockage indisponible */ }
  }, [tab]);

  return (
    <StaffFrame>
      <style>{css}</style>
      <div className="page medium">
        <PageHead title="Messagerie" sub="Réclamations des voyageurs, communication interne et annonces pour le personnel." />

        <div className="msg-tabs" role="tablist">
          {visibleTabs.map(t => {
            const Ic = t.icon;
            return (
              <button key={t.id} role="tab" aria-selected={tab === t.id} className={`msg-tab${tab === t.id ? " active" : ""}`} onClick={() => setTab(t.id)}>
                <Ic size={15}/> <span className="tab-long">{t.label}</span><span className="tab-short">{t.short}</span>
              </button>
            );
          })}
        </div>

        {tab === "INBOX" && canSeeInbox && <ConversationPanel key="inbox" category={null} emptyText="Aucun message de contact ou réclamation pour le moment." />}
        {tab === "INTERNAL" && <ConversationPanel key="internal" category="INTERNAL" placeholder="Écrire au personnel…" emptyText="Aucune conversation interne pour le moment." allowCreate />}
        {tab === "WALL" && <AnnouncementWall canPost={isAdmin} />}
      </div>
    </StaffFrame>
  );
}

/* ═══════════════════════════════════════
   Boîte de réception / communication interne (liste + fil)
   Sur mobile : liste OU fil (avec bouton retour), jamais côte à côte.
═══════════════════════════════════════ */
function ConversationPanel({ category, placeholder, emptyText, allowCreate }) {
  const notify = useNotify();
  const [list, setList]   = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply]   = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [newBody, setNewBody] = useState("");
  const [recipient, setRecipient] = useState(null);
  const [directory, setDirectory] = useState([]);
  const [showDirectory, setShowDirectory] = useState(false);
  const activeRef = useRef(active);
  activeRef.current = active;
  const msgsRef = useRef(null);

  useEffect(() => {
    if (!allowCreate) return;
    api.get("/api/admin/staff-directory").then(r => setDirectory(Array.isArray(r.data) ? r.data : [])).catch(() => setDirectory([]));
  }, [allowCreate]);

  const startWith = (person) => {
    setRecipient(person);
    setNewSubject(person.name);
    setShowDirectory(false);
    setShowNew(true);
  };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = category ? { category } : {};
      const r = await api.get("/api/admin/conversations", { params });
      setList(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      if (!silent) notify.error(errorMessage(e, "Impossible de charger les conversations."));
    } finally { if (!silent) setLoading(false); }
  }, [category, notify]);

  const openThread = useCallback(async (id, silent = false) => {
    if (!silent) setActive(id);
    try {
      const r = await api.get(`/api/admin/conversations/${id}`);
      setThread(r.data);
    } catch (e) {
      if (!silent) { setThread(null); setActive(null); notify.error(errorMessage(e, "Impossible d'ouvrir cette conversation.")); }
    }
  }, [notify]);

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      load(true);
      if (activeRef.current) openThread(activeRef.current, true);
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [load, openThread]);

  // Défile vers le dernier message à l'ouverture / à la réception.
  const msgCount = thread?.messages?.length || 0;
  useEffect(() => { if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight; }, [msgCount, active]);

  const sendReply = async () => {
    if (!reply.trim() || !active || sending) return;
    setSending(true);
    try {
      await api.post(`/api/admin/conversations/${active}/reply`, { body: reply.trim() });
      setReply("");
      openThread(active, true);
      load(true);
    } catch (e) {
      notify.error(errorMessage(e, "Votre message n'a pas pu être envoyé."), { title: "Envoi impossible" });
    } finally { setSending(false); }
  };

  const createInternal = async () => {
    if (!newBody.trim()) { notify.warning("Écrivez un message avant d'envoyer."); return; }
    try {
      const r = await api.post("/api/admin/conversations/internal", {
        subject: newSubject.trim() || "Communication interne",
        body: newBody.trim(),
        recipientUserId: recipient?.id || null,
      });
      setShowNew(false); setNewSubject(""); setNewBody(""); setRecipient(null);
      notify.success(recipient ? `Message envoyé à ${recipient.name}.` : "Message envoyé au personnel.");
      load();
      if (r.data?.id) openThread(r.data.id);
    } catch (e) {
      notify.error(errorMessage(e, "Votre message n'a pas pu être envoyé."), { title: "Envoi impossible" });
    }
  };

  const activeConv = list.find(c => c.id === active);
  const isContactLead = (activeConv?.category || thread?.conversation?.category) === "CONTACT";
  const closeThread = () => { setActive(null); setThread(null); };

  return (
    <div className={`msg-layout${active ? " has-thread" : ""}`}>
      {/* Liste des fils */}
      <div className="msg-pane msg-list-pane">
        {allowCreate && (
          <div className="msg-tools">
            <button className={showDirectory ? "on" : ""} onClick={() => { setShowDirectory(v => !v); setShowNew(false); }}><UsersIcon size={14}/> Personnel</button>
            <button className={showNew ? "on" : ""} onClick={() => { setShowNew(v => !v); setShowDirectory(false); setRecipient(null); }}><PlusIcon size={14}/> Nouveau</button>
          </div>
        )}
        {showDirectory && (
          <div style={{ maxHeight: 240, overflowY: "auto", borderBottom: `1.5px solid ${T.creamDk}` }}>
            {directory.length === 0 && <div style={{ padding: 14, fontSize: 12.5, color: T.smoke }}>Aucun autre membre du personnel pour le moment.</div>}
            {directory.map(p => (
              <button key={p.id} onClick={() => startWith(p)} className="conv" style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between", border: "none", background: "#fff", textAlign: "left", fontFamily: "inherit" }}>
                <div><div className="conv-name">{p.name}</div><div className="conv-sub">{p.roleLabel}</div></div>
                <SendIcon size={13} color={T.smoke}/>
              </button>
            ))}
          </div>
        )}
        {showNew && (
          <div className="new-box">
            {recipient && <div style={{ fontSize: 12.5, color: T.forestDk, fontWeight: 600 }}>À : {recipient.name} ({recipient.roleLabel})</div>}
            <input className="dinput" placeholder="Sujet" value={newSubject} onChange={e => setNewSubject(e.target.value)} />
            <textarea className="dinput dtextarea" style={{ minHeight: 80 }} placeholder="Message…" value={newBody} onChange={e => setNewBody(e.target.value)} />
            <button className="btn btn-primary btn-full" onClick={createInternal}><SendIcon size={14}/> Envoyer</button>
          </div>
        )}
        <div className="msg-list">
          {loading && <div className="loading-block" style={{ padding: 24 }}><div className="spinner"/>Chargement…</div>}
          {!loading && list.length === 0 && <div style={{ padding: 18, fontSize: 13, color: T.smoke }}>{emptyText}</div>}
          {list.map(c => (
            <div key={c.id} className={`conv${active === c.id ? " active" : ""}`} onClick={() => openThread(c.id)} role="button" tabIndex={0}
              onKeyDown={e => (e.key === "Enter" || e.key === " ") && openThread(c.id)}>
              <div className="conv-top">
                <span className="conv-name">{c.guestName || c.createdBy || "Personnel"}</span>
                {c.status === "OPEN" && <span className="conv-dot" aria-label="Non traité" />}
              </div>
              <div className="conv-sub">{c.subject || "(sans sujet)"}</div>
              {c.lastMessage && <div className="conv-last">{c.lastMessage.body}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Fil de discussion */}
      <div className="msg-pane msg-thread-pane">
        {!thread ? (
          <div className="th-empty">{active ? <><div className="spinner" style={{ display: "inline-block", verticalAlign: "middle" }}/> Ouverture…</> : "Sélectionnez une conversation."}</div>
        ) : (
          <>
            <div className="th-head">
              <button className="icon-btn th-back" onClick={closeThread} aria-label="Retour aux conversations">←</button>
              <div style={{ minWidth: 0 }}>
                <div className="th-title">{thread.conversation.subject}</div>
                <div className="th-meta">
                  {thread.conversation.guestName ? `${thread.conversation.guestName}${thread.conversation.guestEmail ? ` (${thread.conversation.guestEmail})` : ""}` : thread.conversation.createdBy}
                </div>
              </div>
            </div>

            {/* Message de contact anonyme : la personne ne peut pas lire une
                réponse dans l'app - on met en avant ses coordonnées. */}
            {isContactLead && (
              <div className="th-lead">
                <p>Ce message vient du formulaire de contact public : la personne n'est pas connectée et ne verra pas de réponse ici. Recontactez-la directement :</p>
                <div className="btn-row">
                  {thread.conversation.guestPhone ? (
                    <>
                      <a href={`tel:${thread.conversation.guestPhone}`} style={{ background: T.forest }}><PhoneIcon size={14}/> Appeler {thread.conversation.guestPhone}</a>
                      <a href={`https://wa.me/${thread.conversation.guestPhone.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" style={{ background: "#25D366" }}><WhatsAppIcon size={14}/> WhatsApp</a>
                    </>
                  ) : (
                    <span style={{ fontSize: 12.5, color: "#92400E" }}>Aucun numéro fourni : répondez par e-mail à {thread.conversation.guestEmail}.</span>
                  )}
                </div>
              </div>
            )}

            <div className="th-msgs" ref={msgsRef}>
              {thread.messages.map(m => {
                const mine = m.authorRole && m.authorRole !== "VISITEUR";
                return (
                  <div key={m.id} className="bubble-wrap" style={{ alignSelf: mine ? "flex-end" : "flex-start" }}>
                    <div className="bubble" style={{ background: mine ? T.forest : "#f1ede3", color: mine ? "#fff" : T.ink }}>{m.body}</div>
                    <div className="bubble-meta" style={{ textAlign: mine ? "right" : "left" }}>{m.authorName} · {new Date(m.createdAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</div>
                  </div>
                );
              })}
            </div>

            {!isContactLead && (
              <div className="th-reply">
                <input className="dinput" value={reply} onChange={e => setReply(e.target.value)} placeholder={placeholder || "Répondre…"}
                  onKeyDown={e => e.key === "Enter" && sendReply()} aria-label="Votre réponse" />
                <button className="btn btn-primary" onClick={sendReply} disabled={sending || !reply.trim()} aria-label="Envoyer"><SendIcon size={14}/><span className="send-label"> Envoyer</span></button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   Mur d'annonces internes (personnel) - actualisation automatique
═══════════════════════════════════════ */
function AnnouncementWall({ canPost }) {
  const notify = useNotify();
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody]   = useState("");
  const [pinned, setPinned] = useState(false);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try { const r = await api.get("/api/admin/announcements"); setItems(Array.isArray(r.data) ? r.data : []); }
    catch (e) { if (!silent) notify.error(errorMessage(e, "Impossible de charger le mur d'annonces.")); }
    finally { if (!silent) setLoading(false); }
  }, [notify]);

  useEffect(() => {
    load();
    const interval = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const post = async () => {
    if (!title.trim() || !body.trim()) { notify.warning("Donnez un titre et un contenu à l'annonce.", { title: "Annonce incomplète" }); return; }
    setPosting(true);
    try {
      await api.post("/api/admin/announcements", { title: title.trim(), body: body.trim(), pinned });
      setTitle(""); setBody(""); setPinned(false);
      notify.success("L'annonce est publiée sur le mur du personnel.");
      load();
    } catch (e) {
      notify.error(errorMessage(e, "La publication a échoué."), { title: "Publication impossible" });
    } finally { setPosting(false); }
  };

  const remove = async (a) => {
    const ok = await notify.confirm({ title: "Supprimer cette annonce ?", danger: true, confirmLabel: "Supprimer",
      message: <>« <strong>{a.title}</strong> » sera retirée du mur pour tout le personnel.</> });
    if (!ok) return;
    try { await api.delete(`/api/admin/announcements/${a.id}`); notify.success("Annonce supprimée."); load(); }
    catch (e) { notify.error(errorMessage(e, "La suppression a échoué.")); }
  };

  const REACTION_EMOJIS = ["👍", "❤️", "😀", "🎉"];

  const react = async (id, emoji) => {
    setItems(prev => prev.map(a => {
      if (a.id !== id) return a;
      const mine = a.myReactions || [];
      const reactions = { ...(a.reactions || {}) };
      const already = mine.includes(emoji);
      reactions[emoji] = Math.max(0, (reactions[emoji] || 0) + (already ? -1 : 1));
      return { ...a, reactions, myReactions: already ? mine.filter(e => e !== emoji) : [...mine, emoji] };
    }));
    try { await api.post(`/api/admin/announcements/${id}/react`, { emoji }); }
    catch { notify.warning("Votre réaction n'a pas pu être enregistrée."); }
    finally { load(true); }
  };

  return (
    <div>
      {canPost && (
        <div className="stf-card">
          <div className="stf-card-title" style={{ fontSize: 16 }}><PinIcon size={16}/> Nouvelle annonce</div>
          <div className="form-stack" style={{ marginTop: 10 }}>
            <input className="dinput" placeholder="Titre de l'annonce" value={title} onChange={e => setTitle(e.target.value)} />
            <textarea className="dinput dtextarea" style={{ minHeight: 80 }} placeholder="Contenu…" value={body} onChange={e => setBody(e.target.value)} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <label className="check-row"><input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} /> Épingler en haut du mur</label>
              <button className="btn btn-primary" onClick={post} disabled={posting}><PinIcon size={14}/> {posting ? "Publication…" : "Publier l'annonce"}</button>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="loading-block"><div className="spinner"/>Chargement…</div>}
      {!loading && items.length === 0 && <div className="empty"><div className="empty-icon"><PinIcon size={28}/></div><p className="empty-text">Aucune annonce pour le moment.</p></div>}
      {items.map(a => (
        <div key={a.id} className={`ann${a.pinned ? " pinned" : ""}`}>
          <div className="ann-head">
            <div className="ann-title">{a.pinned && <PinIcon size={14}/>}{a.title}</div>
            {canPost && (
              <button className="icon-btn danger" onClick={() => remove(a)} aria-label="Supprimer l'annonce" title="Supprimer"><TrashIcon size={14}/></button>
            )}
          </div>
          <p style={{ fontSize: 13.5, color: T.ink, margin: "8px 0", whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.5 }}>{a.body}</p>
          <div className="muted small" style={{ marginBottom: 10 }}>{a.authorName} · {new Date(a.createdAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {REACTION_EMOJIS.map(emoji => {
              const count = a.reactions?.[emoji] || 0;
              const mine = a.myReactions?.includes(emoji);
              return (
                <button key={emoji} className="react" onClick={() => react(a.id, emoji)} aria-pressed={!!mine}
                  style={{ border: `1.5px solid ${mine ? T.forest : T.creamDk}`, background: mine ? "#e8f1ec" : "#fff", color: mine ? T.forestDk : T.smoke, fontWeight: mine ? 700 : 500 }}>
                  <span>{emoji}</span>{count > 0 && <span>{count}</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
